/**
 * Routing.
 *
 * The public routes sit OUTSIDE the authenticated shell and outside
 * AuthProvider's requirements — a customer opening a tracker link has no
 * account, and the page must render without any auth round-trip.
 *
 * The landing route is role-dependent: a technician lands on their tablet
 * queue, a helper on the cage screen, everyone else on the board. Nobody
 * should have to navigate to their own job.
 */

import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { EmptyState, RouteErrorBoundary, SkeletonCard } from "./components/ui";
import { useAuth } from "./lib/auth";
// Every route is lazy. The two public pages are opened by customers on
// mobile data with no account — they must not pull in the staff app, and
// the staff app must not pull in the signature canvas.
const Shell = lazy(() => import("./components/Shell").then((m) => ({ default: m.Shell })));
const BoardPage = lazy(() => import("./pages/Board").then((m) => ({ default: m.BoardPage })));
const TabletPage = lazy(() => import("./pages/Tablet").then((m) => ({ default: m.TabletPage })));
const CagePage = lazy(() => import("./pages/Cage").then((m) => ({ default: m.CagePage })));
const InspectionPage = lazy(() => import("./pages/Inspection").then((m) => ({ default: m.InspectionPage })));
const JobDetailPage = lazy(() => import("./pages/JobDetail").then((m) => ({ default: m.JobDetailPage })));
const NewEnquiryPage = lazy(() => import("./pages/NewEnquiry").then((m) => ({ default: m.NewEnquiryPage })));
const CustomersPage = lazy(() => import("./pages/Customers").then((m) => ({ default: m.CustomersPage })));
const InventoryPage = lazy(() => import("./pages/Inventory").then((m) => ({ default: m.InventoryPage })));
const TeamPage = lazy(() => import("./pages/Team").then((m) => ({ default: m.TeamPage })));
const SettingsPage = lazy(() => import("./pages/Settings").then((m) => ({ default: m.SettingsPage })));
const JobsPage = lazy(() => import("./pages/Jobs").then((m) => ({ default: m.JobsPage })));
const SuppliersPage = lazy(() => import("./pages/Suppliers").then((m) => ({ default: m.SuppliersPage })));
const EnquiriesPage = lazy(() => import("./pages/Enquiries").then((m) => ({ default: m.EnquiriesPage })));
const PurchaseOrdersPage = lazy(() => import("./pages/PurchaseOrders").then((m) => ({ default: m.PurchaseOrdersPage })));
const PurchaseInvoicesPage = lazy(() => import("./pages/PurchaseInvoices").then((m) => ({ default: m.PurchaseInvoicesPage })));
const ExpensesPage = lazy(() => import("./pages/Expenses").then((m) => ({ default: m.ExpensesPage })));
const BillingPage = lazy(() => import("./pages/Billing").then((m) => ({ default: m.BillingPage })));
const SignInLanding = lazy(() =>
  import("./pages/SignInLanding").then((m) => ({ default: m.SignInLanding })),
);
const ClerkRoot = lazy(() => import("./lib/ClerkRoot").then((m) => ({ default: m.ClerkRoot })));
const SignOutButton = lazy(() =>
  import("./lib/ClerkRoot").then((m) => ({ default: m.SignOutButton })),
);
const SuperadminPage = lazy(() => import("./pages/Superadmin").then((m) => ({ default: m.SuperadminPage })));
const InvoicesPage = lazy(() => import("./pages/Invoices").then((m) => ({ default: m.InvoicesPage })));
const ReportsPage = lazy(() => import("./pages/Reports").then((m) => ({ default: m.ReportsPage })));
const SetupWizardPage = lazy(() => import("./pages/SetupWizard").then((m) => ({ default: m.SetupWizardPage })));
const QuotePage = lazy(() => import("./pages/public/Quote").then((m) => ({ default: m.QuotePage })));
const TrackerPage = lazy(() => import("./pages/public/Tracker").then((m) => ({ default: m.TrackerPage })));
const InviteAcceptPage = lazy(() =>
  import("./pages/public/InviteAccept").then((m) => ({ default: m.InviteAcceptPage })),
);
import { LogOut, Wrench } from "lucide-react";

export function App() {
  // A route that failed to load once (a stale chunk, most often) triggered a
  // one-time reload in RouteErrorBoundary. Having gotten this far, this is a
  // fresh, successful load — clear the guard so a genuinely new failure later
  // in the same tab can still self-heal once, instead of the flag from an
  // unrelated failure minutes ago silently disarming the recovery.
  useEffect(() => {
    sessionStorage.removeItem("garageos.chunkReload");
  }, []);

  return (
    <BrowserRouter>
      <RoutedContent />
    </BrowserRouter>
  );
}

/**
 * Split out from `App` because `useLocation()` only works below
 * `<BrowserRouter>` — and the current path is what tells the error boundary
 * a new route is being attempted, so a crash on one section doesn't keep
 * showing its fallback for every section clicked after it.
 */
function RoutedContent() {
  const { pathname } = useLocation();

  return (
    <RouteErrorBoundary resetKey={pathname}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — no auth, no shell, and critically no Clerk SDK: these are
            opened by a customer with no account, often on mobile data. */}
        <Route path="/track/:token" element={<TrackerPage />} />
        <Route path="/quote/:token" element={<QuotePage />} />

        {/* Everything a member of staff reaches, including the invite-accept
            page, sits behind ClerkRoot — its own lazy chunk, so the two
            public routes above never pull it in. */}
        <Route element={<ClerkRoot />}>
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/*" element={<AuthedApp />} />
        </Route>
      </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}

/** Skeleton while a route chunk loads. Never a spinner, never a jump. */
function RouteFallback() {
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <div className="skeleton h-8 w-56" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

function AuthedApp() {
  const { session, loading, error, garage, role } = useAuth();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <div className="skeleton h-8 w-56" />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  // Nobody signed in — the real front door.
  if (error || !session) {
    return <SignInLanding />;
  }

  // A superadmin with no garage of their own belongs in the platform console,
  // not in a garage shell that has no tenant to point at. One who IS also a
  // member of a garage keeps both, and reaches the console by URL.
  if (session.is_superadmin && session.garages.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {/* This branch has no <Shell> — a pure superadmin has no garage for
            Shell's nav to be scoped to — so without this, there was no sign
            out control anywhere on screen for them at all. */}
        <header
          className="flex items-center justify-between border-b border-line
                     bg-elevated/90 px-4 py-3 backdrop-blur"
        >
          <span className="text-sm font-semibold">GarageOS · Platform console</span>
          <SignOutButton className="btn-ghost">
            <LogOut className="h-4 w-4" />
            Sign out
          </SignOutButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Routes>
            <Route path="/superadmin" element={<SuperadminPage />} />
            <Route path="*" element={<Navigate to="/superadmin" replace />} />
          </Routes>
        </div>
      </div>
    );
  }

  if (session.garages.length === 0 && !session.is_superadmin) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <div className="w-full max-w-md">
          <EmptyState
            icon={Wrench}
            title="You're not part of a garage yet"
            description="Ask your garage admin to invite you, then open the link they send."
          />
        </div>
      </div>
    );
  }

  // A garage that hasn't finished setup goes straight to the wizard.
  //
  // /team is let through this gate on purpose: the wizard's own team step
  // says "invite your staff" and links there, and a redirect straight back
  // to /setup would make that link a lie — an admin bounced off the one
  // screen the wizard just sent them to.
  if (garage && !garage.setup_completed && role === "admin") {
    return (
      <Routes>
        <Route element={<Shell />}>
          <Route path="team" element={<TeamPage />} />
        </Route>
        <Route path="/setup" element={<SetupWizardPage />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Landing />} />
        <Route path="board" element={<BoardPage />} />
        <Route path="tablet" element={<TabletPage />} />
        <Route path="cage" element={<CagePage />} />
        <Route path="jobs/new" element={<NewEnquiryPage />} />
        <Route path="jobs/:jobId" element={<JobDetailPage />} />
        <Route path="jobs/:jobId/inspection" element={<InspectionPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="enquiries" element={<EnquiriesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="supplier-bills" element={<PurchaseInvoicesPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {/* Only registered for an actual superadmin. The API refuses anyone
            else anyway, but rendering the console's chrome to a garage admin
            before its queries 403 is a needless glimpse of a screen that is
            none of their business. */}
        {session.is_superadmin && (
          <Route path="superadmin" element={<SuperadminPage />} />
        )}
        <Route path="reports" element={<ReportsPage />} />
        <Route path="setup" element={<SetupWizardPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Land people where their work is. */
function Landing() {
  const { role } = useAuth();
  if (role === "technician") return <Navigate to="/tablet" replace />;
  if (role === "helper") return <Navigate to="/cage" replace />;
  return <Navigate to="/board" replace />;
}

/**
 * The catch-all. Every navigation destination now has a real screen, so
 * reaching this means a typed or stale URL, not an unfinished feature — and it
 * should say so rather than implying something is still coming.
 */
function NotFound() {
  return (
    <EmptyState
      icon={Wrench}
      title="Page not found"
      description="That address doesn't match anything. Use the menu to get back to your work."
    />
  );
}
